import {
  ClearingUpdateStrategy,
  ManualUpdateStrategy,
  Pagination,
  RegexScanUpdateStrategy,
  UpdaterProfile,
  UpdateStrategyType,
} from '@ekkogmbh/apisdk';
import { Add, Delete, Edit } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import Permissions from 'src/Common/Permissions';
import { UpdaterProfilePanel } from './UpdaterProfilePanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import ObjectField, { ObjectFieldItem, ObjectFieldWrapperProps } from 'src/Common/Components/DataTable/ObjectField';
import CompartmentSelectorChip from 'src/CompartmentSelectorManagement/Components/CompartmentSelectorChip';
import useRequest from 'src/Common/Hooks/useRequest';
import { useContentControl } from 'src/Common/Stores/ContentControlProvider';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import ScrollableStack from 'src/Common/Components/ScrollableStack';

const ROW_MIN_HEIGHT = 64;

const ScrollableFieldWrapper = (props: ObjectFieldWrapperProps): React.JSX.Element => (
  <ScrollableStack minHeight={ROW_MIN_HEIGHT} width="100%" {...props} />
);

export const UpdaterProfileManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { setPanelOpen } = useContentControl();
  const [, startRequest] = useRequest();

  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedUpdaterProfile, setSelectedProfile] = useState<UpdaterProfile>();

  const dismiss = useCallback(() => {
    setSelectedProfile(undefined);
    setDialogOpen(false);
    setPanelOpen(false);
  }, []);

  const showPanel = useCallback((updaterProfile?: UpdaterProfile) => {
    setDialogOpen(false);
    setSelectedProfile(updaterProfile);
    setPanelOpen(true);
  }, []);

  const deleteProfile = useCallback(() => {
    const { name, coordinate } = selectedUpdaterProfile!;
    startRequest(
      () => api.deleteUpdaterProfile(coordinate, name),
      () => {
        enqueueSnackbar('Updater Profile deleted');
        dismiss();
        refresh();
      },
    );
  }, [api, selectedUpdaterProfile, startRequest, refresh]);

  const fetchItems = useCallback((pagination: Pagination) => api.getUpdaterProfiles(pagination), [api]);

  return (
    <ContentControl
      panel={<UpdaterProfilePanel profile={selectedUpdaterProfile} closeHandler={dismiss} />}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Updater Profile'}
          text={selectedUpdaterProfile?.name ?? ''}
          onClose={dismiss}
          onConfirm={deleteProfile}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Updater Profile'}
          onClick={() => showPanel(undefined)}
          requiredPermission={Permissions.UPDATER_PROFILE_WRITE}
        />,
      ]}
    >
      <DataTable
        fetchItems={fetchItems}
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: UpdaterProfile) => showPanel(entry),
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: UpdaterProfile) => {
              setSelectedProfile(entry);
              setDialogOpen(true);
            },
          },
        ]}
        columns={[
          {
            header: 'Name',
            body: (entry: UpdaterProfile) => entry.name,
            filterField: 'name',
            sortField: 'UP.name',
            fitContent: true,
          },
          {
            header: 'Coordinate',
            body: (entry: UpdaterProfile, searchText?: string) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                {searchText ? dataTableHighlight(entry.coordinate, [searchText]) : entry.coordinate}
              </ScrollableStack>
            ),
            filterField: 'coordinate',
            sortField: 'N.materializedPath',
            proportionalContent: '55%',
          },
          {
            header: 'Type',
            body: (entry: UpdaterProfile) => entry.updateStrategy.type,
            fitContent: true,
          },
          {
            header: 'Compartment-Selector',
            body: (entry: UpdaterProfile, searchText?: string) => (
              <CompartmentSelectorChip compartmentSelector={entry.compartmentSelector} highlightText={searchText} />
            ),
            fitContent: true,
          },
          {
            header: 'Input-Instructions',
            body: (entry: UpdaterProfile, searchText?: string) => (
              <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
                {searchText
                  ? dataTableHighlight(entry.compartmentInputInstructions ?? '-', [searchText])
                  : (entry.compartmentInputInstructions ?? '-')}
              </ScrollableStack>
            ),
            proportionalContent: '45%',
          },
          {
            header: 'Configuration',
            body: (entry: UpdaterProfile) => {
              const { updateStrategy } = entry;
              let items: ObjectFieldItem[];

              switch (updateStrategy.type) {
                case UpdateStrategyType.MANUAL: {
                  const { preset, editMode, disabledFields } = (updateStrategy as ManualUpdateStrategy).params;
                  items = [
                    { key: 'Preset', value: preset },
                    { key: 'Edit Mode', value: editMode },
                    { key: 'Disabled Fields', value: disabledFields.join(', ') },
                  ];
                  break;
                }
                case UpdateStrategyType.REGEX_SCAN: {
                  const { fields } = (updateStrategy as RegexScanUpdateStrategy).params;
                  items = [{ key: 'Regex Fields', value: fields.map((f) => f.key).join(', ') }];
                  break;
                }
                case UpdateStrategyType.CLEARING: {
                  const { preset, disabledFields, clearingText } = (updateStrategy as ClearingUpdateStrategy).params;
                  items = [
                    { key: 'Preset', value: preset },
                    { key: 'Keep Fields', value: disabledFields.join(', ') },
                    { key: 'Clearing Text', value: clearingText },
                  ];
                  break;
                }
              }

              return (
                <ObjectField
                  direction={'column'}
                  fields={items}
                  useWrapper={ScrollableFieldWrapper}
                  extraEntryStyle={{ margin: 8 }}
                />
              );
            },
            fitContent: true,
          },
        ]}
      />
    </ContentControl>
  );
};
